import { ReactElement, useContext, useState, useEffect } from 'react';
import { Field, getIn, useFormikContext } from 'formik';
import { CheckboxInputAccessible, DropdownMenu, TextInput } from '@components';
import { iCountry } from '@interfaces/interfaces';
import { AusStateList } from '@constants';
import VicFinesFormContext from '@pages/forms/VicFinesFormContext';
import { iThirdPartyFormValues } from '@services/ThirdPartyService';
import { iCourtFormValues } from '@services/CourtService';
import classnames from 'classnames';
import styles from './LicenceField.module.scss';

interface iProps {
  name: string;
  title: string;
  nameStateOfIssue: string;
  nameCountryOfIssue: string;
  nameNoDriverLicence: string;
  isMandatory?: boolean;
  noDriverLicenceOptRequired?: boolean;
  noDriverLicenceTitle?: string;
}

const LicenceFieldAccessible = ({
  title,
  name,
  nameCountryOfIssue,
  nameStateOfIssue,
  nameNoDriverLicence,
  isMandatory = false,
  noDriverLicenceOptRequired = true,
  noDriverLicenceTitle = 'I do not currently hold a driver licence',
}: iProps): ReactElement => {
  const { values, errors, touched }: { values: iThirdPartyFormValues | iCourtFormValues; errors: any; touched: any } = useFormikContext();
  const [disableLicenceInput, setLicenceInput] = useState(false);
  const formikContext = useFormikContext();
  const { countryList } = useContext(VicFinesFormContext);

  const COUNTRY_DROPDOWN = countryList.map((item: iCountry): { value: string; display: string } => ({
    value: item.Name,
    display: item.Name,
  }));

  useEffect(() => {
    if (getIn(values, nameNoDriverLicence)) {
      setLicenceInput(true);
    } else {
      setLicenceInput(false);
    }
  }, [getIn(values, nameNoDriverLicence)]);

  return (
    <div className={styles.container}>
      <div className={styles.licenceInput}>
        <span
          className={classnames({
            [styles.titleBold]: true,
            [styles.error]: getIn(errors, name) && getIn(touched, name),
          })}>
          {title}
          {!isMandatory && <div className={styles.licenceOptional}>( optional )</div>}
        </span>
        <span
          id="poidesktop"
          className={classnames({
            [styles.titleBold]: true,
            [styles.error]: getIn(errors, nameCountryOfIssue) && getIn(touched, nameCountryOfIssue),
          })}>
          Place of issue
          {!isMandatory && <span className={styles.optional}> ( optional )</span>}
        </span>
        <Field name={name} component={TextInput} isMandatory disabled={disableLicenceInput} />
        <Field name={nameCountryOfIssue} component={DropdownMenu} displayText={COUNTRY_DROPDOWN} isMandatory disabled={disableLicenceInput} />
        <div>
          {getIn(values, nameCountryOfIssue) === 'Australia' ? (
            <Field name={nameStateOfIssue} component={DropdownMenu} displayText={AusStateList} disabled={disableLicenceInput} />
          ) : (
            <div className={styles.textBox}>International</div>
          )}
        </div>
      </div>
      {noDriverLicenceOptRequired && (
        <Field
          name={nameNoDriverLicence}
          component={CheckboxInputAccessible}
          title={noDriverLicenceTitle}
          boxChecked={(e: boolean) => {
            setLicenceInput(e);
            formikContext.setFieldTouched(name, false);
            formikContext.setFieldValue(name, '');
            formikContext.setFieldTouched(nameCountryOfIssue, false);
            formikContext.setFieldTouched(nameStateOfIssue, false);
          }}
        />
      )}
    </div>
  );
};

export default LicenceFieldAccessible;
